<script lang="ts">
  import { options } from '../../store';
  import Icon from '../icon/icon.svelte';
  import { gameTokeniser } from '../utils/text-vocabulary';

  interface Props {
    data?: string;
    // Whether a keyword opens its tip. Off where the text already sits in a
    // tip, since a tip cannot open inside another.
    interactive?: boolean;
    // A span rather than a paragraph, for text that sits inside a sentence or
    // another paragraph -- a <p> there splits the paragraph around it.
    inline?: boolean;
    class?: string;
  }

  let { data = '', interactive = true, inline = false, class: clazz = '' }: Props = $props();

  // The splitting lives in utils/text-tokens.ts, where it is tested; the
  // words it looks for are shared by every Text on screen.
  let tokens = $derived(gameTokeniser(!!$options.tooltipsKeywords)(data ?? ''));
</script>

<!--
  A keyword is only a marked span. Its tip is drawn by the one TipLayer in
  App, which watches the whole document -- no component per keyword.
-->
{#if inline}
  <span class="text {clazz}"
    >{#each tokens as token}{#if token.type === 'text'}{token.text}{:else if token.type === 'icon'}<Icon
          name={token.name}
          class="icon"
        />{:else if interactive}<span
          class="keyword interactive"
          data-tip-kind={token.type}
          data-tip-key={token.key}
          role="button"
          tabindex="0"
          aria-describedby="game-tip">{token.text}</span
        >{:else}<span class="keyword">{token.text}</span>{/if}{/each}</span
  >
{:else}
  <section class={clazz}>
    <p class="p">
      {#each tokens as token}{#if token.type === 'text'}{token.text}{:else if token.type === 'icon'}<Icon
            name={token.name}
            class="icon"
          />{:else if interactive}<span
            class="keyword interactive"
            data-tip-kind={token.type}
            data-tip-key={token.key}
            role="button"
            tabindex="0"
            aria-describedby="game-tip">{token.text}</span
          >{:else}<span class="keyword">{token.text}</span>{/if}{/each}
    </p>
  </section>
{/if}

<style lang="less">
  @import '../../defines.less';

  p {
    line-height: 1.5;
    margin: 0;
    font-weight: normal;
  }

  // The one keyword style for the whole game. Anything that renders game
  // text goes through this component, so style keywords here, not at the
  // call site.
  .keyword {
    position: relative;
    // Inline, not inline-block: ::first-letter cannot reach into an
    // inline-block, so a keyword opening a paragraph lost its drop cap.
    display: inline;
    font-weight: bold;
    color: @edge-shine;

    // A faint dotted line where hovering opens the keyword's tip.
    &.interactive {
      text-decoration: underline dotted fade(@edge-shine, 55%);
      text-underline-offset: 3px;
      cursor: help;

      // Only for the keyboard; a click or tap shows no ring.
      &:focus-visible {
        outline: 2px solid fade(@edge-shine, 70%);
        outline-offset: 2px;
        border-radius: 2px;
      }
    }
  }
</style>
